import { useInfiniteQuery } from '@tanstack/react-query';
import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import type { PaymentLinkPaymentsResponse } from '@repo/api-kit/queries/payment-links';
import { client as qontoClient } from '#utils/client';

const paymentLinkV2Namespace = 'v2';
const isTesting = getEnvironmentVariables().environment === 'test';
const DEFAULT_ITEMS_PER_PAGE = 50;

interface FetchPaymentArgs {
  page?: number;
  perPage?: number;
  sortBy?: string;
  paymentLinkId: string;
  enabled?: boolean;
}

export async function getPayments(
  page: number,
  perPage: number,
  sortBy: string,
  paymentLinkId: string
): Promise<PaymentLinkPaymentsResponse> {
  const params: Record<string, string> = {
    'page[number]': page.toString(),
    'page[size]': perPage.toString(),
    'filter[status]': 'paid',
    sort: sortBy,
  };

  const responseData = await qontoClient<PaymentLinkPaymentsResponse>(
    `${paymentLinkV2Namespace}/payment_links/${paymentLinkId}/payments`,
    {
      params,
    }
  );

  return responseData;
}

export function useFetchInfinitePayments({
  perPage = DEFAULT_ITEMS_PER_PAGE,
  sortBy = '-creation_date',
  paymentLinkId,
  enabled = true,
}: Omit<FetchPaymentArgs, 'page'>) {
  return useInfiniteQuery({
    queryKey: ['infinite-payments', sortBy, perPage, paymentLinkId],
    queryFn: ({ pageParam }) => getPayments(pageParam, perPage, sortBy, paymentLinkId),
    getNextPageParam: lastPage => lastPage.meta?.next_page,
    initialPageParam: 1,
    enabled,
    retry: isTesting ? false : 3,
  });
}
